import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";

type Redirect = { from_path: string; to_path: string; status_code: number };

let cache: Redirect[] | null = null;
let inflight: Promise<Redirect[]> | null = null;

async function loadRedirects(): Promise<Redirect[]> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = fetch("/api/public/redirects-json")
    .then((r) => (r.ok ? r.json() : []))
    .then((items: Redirect[]) => {
      cache = Array.isArray(items) ? items : [];
      return cache;
    })
    .catch(() => {
      cache = [];
      return cache;
    });
  return inflight;
}

function normalise(p: string): string {
  if (!p) return "/";
  let out = p.trim();
  if (!out.startsWith("/")) out = "/" + out;
  if (out.length > 1 && out.endsWith("/")) out = out.slice(0, -1);
  return out.toLowerCase();
}

/**
 * Client-side redirect handler. Reads the active redirect map from the
 * `/api/public/redirects-json` endpoint (driven by the CMS `redirects` table)
 * and replaces the current location for any legacy URL that matches.
 *
 * Server-side / hosting 301s are still preferred for SEO, but this
 * guarantees that redirects added via the admin UI take effect immediately
 * for users (and bots that execute JS) without a deploy.
 */
export function RedirectGate() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    void loadRedirects().then(() => setReady(true));
  }, []);

  useEffect(() => {
    if (!ready || typeof window === "undefined") return;
    if (!cache || cache.length === 0) return;
    const target = normalise(pathname);
    const match = cache.find((r) => normalise(r.from_path) === target);
    if (!match) return;
    const to = match.to_path.startsWith("http")
      ? match.to_path
      : (match.to_path.startsWith("/") ? match.to_path : "/" + match.to_path);
    if (normalise(to) === target) return;
    window.location.replace(to + window.location.search + window.location.hash);
  }, [pathname, ready]);

  return null;
}
